import { Product, Variant } from "../../../../types/types";
import { getDefaultCategory } from "../../../../utils/product/getDefaultCategory";
import { dispatchAnalyticsEvent } from "../../../../utils";
import { MutableRefObject, RefObject } from "react";

const handleTimeUpdate = (
  type: string,
  videoRef: RefObject<HTMLVideoElement>,
  videoId: string,
  videoTimeline: MutableRefObject<{
    started: boolean;
    reached50: boolean;
    videoCompleted: boolean;
  }>,
  product: Product,
  selectedVariant: Variant,
) => {
  if (videoRef && videoRef.current) {
    const percentageOfVideoWatched =
      (videoRef.current.currentTime / videoRef.current.duration) * 100;

    if (percentageOfVideoWatched >= 1 && !videoTimeline.current.started) {
      videoTimeline.current.started = true;
      dispatchAnalyticsEvent(
        "pdp.videoPlay",
        getVideoAnalyticsPayload(
          type,
          product,
          selectedVariant,
          videoId,
          false,
          false,
        ),
        {},
      );
    }
    if (percentageOfVideoWatched >= 50 && !videoTimeline.current.reached50) {
      videoTimeline.current.reached50 = true;
      dispatchAnalyticsEvent(
        "pdp.videoPlay50",
        getVideoAnalyticsPayload(
          type,
          product,
          selectedVariant,
          videoId,
          false,
          false,
        ),
        {},
      );
    }
    if (
      percentageOfVideoWatched >= 80 &&
      !videoTimeline.current.videoCompleted
    ) {
      videoTimeline.current.videoCompleted = true;
      dispatchAnalyticsEvent(
        "pdp.videoCompleted",
        getVideoAnalyticsPayload(
          type,
          product,
          selectedVariant,
          videoId,
          false,
          false,
        ),
        {},
      );
    }
  }
};

function getTimeUpdateHandler(
  type: string,
  videoRef: RefObject<HTMLVideoElement>,
  videoId: string,
  videoTimeline: MutableRefObject<{
    started: boolean;
    reached50: boolean;
    videoCompleted: boolean;
  }>,
  product: Product,
  selectedVariant: Variant,
) {
  return function () {
    handleTimeUpdate(
      type,
      videoRef,
      videoId,
      videoTimeline,
      product,
      selectedVariant,
    );
  };
}

function getVideoAnalyticsPayload(
  type: string,
  product: Product,
  selectedVariant: Variant,
  videoId: string,
  isModal: boolean,
  isAutoPlay: boolean,
) {
  return {
    type: type,
    additionalInfo: { isModal: isModal, autoPlay: isAutoPlay },
    properties: { videoId: videoId },
    products: [
      {
        brand: product.brand?.name,
        category: getDefaultCategory(product),
        price: selectedVariant.price?.value,
        productName: selectedVariant.title,
        productId: product.aliases.pimProductId,
        sku: selectedVariant.aliases.skuId,
      },
    ],
  };
}

function getZoomAnalyticsPayload(
  type: string,
  product: Product,
  selectedVariant: Variant,
) {
  return {
    type: type,
    products: [
      {
        // why does the compiler think brand may be undefined here? It's mandatory on product
        brand: product.brand?.name,
        category: getDefaultCategory(product),
        price: selectedVariant.price?.value,
        productName: selectedVariant.title,
        productId: product.aliases.pimProductId,
        sku: selectedVariant.aliases.skuId,
      },
    ],
  };
}

interface AnalyticsProductData {
  enableAnalytics: boolean;
  brandName: string | undefined;
  category: string;
  price: string | undefined;
  productName: string;
  productId: string;
  sku: string;
}

const getAnalyticsProductData = (
  product: Product,
  selectedVariant: Variant,
  enableAnalytics: boolean,
) => {
  const productData: AnalyticsProductData = {
    brandName: product.brand?.name,
    category: getDefaultCategory(product),
    price: selectedVariant.price?.value,
    productName: selectedVariant.title,
    productId: product.aliases.pimProductId,
    sku: selectedVariant.aliases.skuId,
    enableAnalytics: enableAnalytics,
  };

  return productData;
};

export {
  getTimeUpdateHandler,
  getVideoAnalyticsPayload,
  getZoomAnalyticsPayload,
  getAnalyticsProductData,
};
